import axios from 'axios';
import dayjs from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import {
  BASE_URL_NODE,
  PRODUCT_COUNT_LOG,
  USING_ACTIVE_BOGO_LIST_API,
} from './Constants/Config';
import {
  isProductCouponEligible,
  isCartItemCouponEligible,
  isMkonnektDiscountedItem,
  isApplicableCoupon,
  couponEligibleSubtotal,
  calculateCouponDiscount,
  couponDeductionPercentage,
} from './couponEligibility';
import {
  normalizeMkonnektUpc,
  resolveMkonnektMerchantId,
} from './redux/features/MkonnektDiscount/mkonnektDiscountSlice';

dayjs.extend(customParseFormat);

// Re-export coupon eligibility helpers so existing `from '.../utils'` imports work.
export {
  isProductCouponEligible,
  isCartItemCouponEligible,
  isMkonnektDiscountedItem,
  isApplicableCoupon,
  couponEligibleSubtotal,
  calculateCouponDiscount,
  couponDeductionPercentage,
};

export function zeroOrNumber(input) {
  const number = input && !isNaN(input) && parseFloat(input) > 0;
  return number ? parseFloat(input) : 0;
}

const DEZI_META_KEY_PREFIX = 'dezi_';

const readStoredMerchantId = () => {
  try {
    return localStorage.getItem('merchant_id');
  } catch {
    return null;
  }
};

// The merchant whose Dezi flag applies: on checkout (/payment-cart/:id) the
// route store, same as the mKonnekt quote (resolveMkonnektMerchantId); elsewhere
// the last-visited merchant.
const readMerchantId = () => {
  let pathname = '';
  try {
    pathname = window.location.pathname;
  } catch {
    pathname = '';
  }
  return resolveMkonnektMerchantId(pathname, readStoredMerchantId());
};

// Merchant ids arrive from the URL, localStorage and API payloads; normalise
// once so comparisons and dezi cache keys agree regardless of source.
const normalizeDeziMerchantId = (merchantId) => String(merchantId ?? '').trim();

const readDeziMeta = (rawMerchantId) => {
  const merchantId = normalizeDeziMerchantId(rawMerchantId);
  if (!merchantId) return null;

  try {
    const raw = localStorage.getItem(`${DEZI_META_KEY_PREFIX}${merchantId}`);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
};

export const cacheDeziMeta = (rawMerchantId, storeData) => {
  const merchantId = normalizeDeziMerchantId(rawMerchantId);
  if (!merchantId || storeData?.is_dezi_enabled === undefined) return;

  try {
    localStorage.setItem(
      `${DEZI_META_KEY_PREFIX}${merchantId}`,
      JSON.stringify({
        is_dezi_enabled: String(storeData.is_dezi_enabled) === '1' ? '1' : '0',
        rcn: storeData?.rcn ?? '',
      }),
    );
  } catch {
    // Storage can be unavailable in privacy-restricted browser contexts.
  }
};

export const readCachedDeziFlag = (merchantId) =>
  readDeziMeta(merchantId)?.is_dezi_enabled === '1';

export const readCachedRcn = (merchantId) =>
  readDeziMeta(merchantId)?.rcn || '';

/**
 * The store's mKonnekt RCN, or '' when this merchant has none provisioned.
 *
 * NEVER falls back to merchantId. The RCN is mKonnekt's own store identifier and a
 * QuickVee merchant id is not one: quoting with it returns discounts for a store
 * mKonnekt has never heard of (silently empty), and reporting the sale with it is a
 * hard 403 from the backend's rcn-ownership check. Callers must SKIP the call on ''
 * rather than substitute something that merely looks like an id.
 *
 * `storeData` is only trusted when it is the cart merchant's own store — the cart
 * drawer opens on pages that load a different store (brand pages) or none at all, so
 * the value cached the last time this merchant's store did load is the right fallback.
 */
export const resolveDeziRcn = (storeData, rawMerchantId) => {
  const merchantId = normalizeDeziMerchantId(rawMerchantId);
  if (!merchantId) return '';

  const storeMerchantId = normalizeDeziMerchantId(storeData?.merchant_id);
  const storeIsForThisMerchant =
    Boolean(storeMerchantId) && storeMerchantId === merchantId;
  const rcn = storeIsForThisMerchant
    ? storeData?.rcn || readCachedRcn(merchantId)
    : readCachedRcn(merchantId);

  return String(rcn ?? '').trim();
};

export const selectIsDeziEnabled = (state) => {
  const storeData = state?.singleStores?.storesData?.store_data;
  const cartMerchantId = normalizeDeziMerchantId(readMerchantId());
  const storeDataMatchesCart =
    !cartMerchantId ||
    normalizeDeziMerchantId(storeData?.merchant_id) === cartMerchantId;

  if (storeData?.is_dezi_enabled !== undefined && storeDataMatchesCart) {
    return String(storeData.is_dezi_enabled) === '1';
  }

  return readCachedDeziFlag(cartMerchantId);
};

export function formatNumber(input) {
  if (!input) return '0.00';

  // Parse input as a number
  const num = parseFloat(input);

  // Round to 3 decimal places
  let rounded = num.toFixed(3);

  // Ensure the result has at least two decimal places
  rounded = rounded.replace(/(\.\d{2})0+$/, '$1'); // Remove trailing zeros after the second decimal place
  rounded = rounded.replace(/(\.\d{1})$/, '$10'); // Add a zero if there's only one digit after the decimal

  return rounded;
}

const isGreaterThan10 = (num) => (+num >= 10 ? num : `0${num}`);

/**
 * Parse calendar date strings as local midnight (no UTC shift).
 * ISO "YYYY-MM-DD" from APIs is parsed as UTC by `new Date()`, which shifts
 * the displayed day in negative-offset timezones.
 */
export function parseDateOnlyLocal(dateString) {
  if (dateString == null || dateString === '') return null;
  const s = String(dateString).trim();
  if (!s || s === '0000-00-00') return null;

  const isoMatch = /^(\d{4})-(\d{1,2})-(\d{1,2})$/.exec(s);
  if (isoMatch) {
    const y = parseInt(isoMatch[1], 10);
    const m = parseInt(isoMatch[2], 10);
    const d = parseInt(isoMatch[3], 10);
    const dt = new Date(y, m - 1, d);
    if (dt.getFullYear() === y && dt.getMonth() === m - 1 && dt.getDate() === d) {
      return dt;
    }
    return null;
  }

  const usMatch = /^(\d{1,2})\/(\d{1,2})\/(\d{4})$/.exec(s);
  if (usMatch) {
    const m = parseInt(usMatch[1], 10);
    const d = parseInt(usMatch[2], 10);
    const y = parseInt(usMatch[3], 10);
    const dt = new Date(y, m - 1, d);
    if (dt.getFullYear() === y && dt.getMonth() === m - 1 && dt.getDate() === d) {
      return dt;
    }
    return null;
  }

  const fallback = new Date(s);
  return isNaN(fallback.getTime()) ? null : fallback;
}

/** Display-only: MM/DD/YYYY from YYYY-MM-DD or M/D/YYYY input. */
export function formatDateOnlyMMDDYYYY(dateString) {
  const d = parseDateOnlyLocal(dateString);
  if (!d) return '';
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  const yyyy = d.getFullYear();
  return `${mm}/${dd}/${yyyy}`;
}

/** Normalized YYYY-MM-DD for API payloads from date-picker or ISO date strings. */
export function formatDateOnlyYYYYMMDD(dateString) {
  const d = parseDateOnlyLocal(dateString);
  if (!d) return '';
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

export const formatDate = (inputDate, type) => {
  let parsedTime = null;
  if (typeof inputDate === 'string' && inputDate.trim()) {
    parsedTime = parseDateOnlyLocal(inputDate.trim());
  }
  if (!parsedTime || isNaN(parsedTime.getTime())) {
    parsedTime = inputDate instanceof Date ? inputDate : new Date(inputDate);
  }
  if (isNaN(parsedTime.getTime())) {
    return 'Invalid time format';
  }

  const day = parsedTime.getDate();
  const month = parsedTime.toLocaleString('default', { month: 'short' });
  const year = parsedTime.getFullYear();
  let hours = parsedTime.getHours();
  const minutes = parsedTime.getMinutes();
  const seconds = parsedTime.getSeconds();
  const meridiem = hours >= 12 ? 'PM' : 'AM';
  hours = hours % 12 || 12;

  let formattedTime = `${month} ${day}, ${year} ${isGreaterThan10(
    hours,
  )}:${isGreaterThan10(minutes)}:${isGreaterThan10(seconds)} ${meridiem}`;

  if (type === 'expiry-date') {
    formattedTime = `${month} ${day}, ${year}`;
  }

  if (type === 'future-order') {
    formattedTime = `${month} ${day}, ${year} ${isGreaterThan10(
      hours,
    )}:${isGreaterThan10(minutes)} ${meridiem}`;
  }
  return formattedTime;
};

export function toDayjsOrNull(value, format) {
  if (value == null || value === '') return null;
  if (dayjs.isDayjs(value)) return value.isValid() ? value : null;
  const parsed = format ? dayjs(value, format) : dayjs(value);
  return parsed?.isValid?.() ? parsed : null;
}

export function formatDateTimeOnly(dateTime, usingFor) {
  const date = new Date(dateTime);

  const day = String(date.getDate()).padStart(2, '0'); // Add leading zero
  const month = String(date.getMonth() + 1).padStart(2, '0'); // Add leading zero
  const year = date.getFullYear();
  let hours = date.getHours();
  const minutes = String(date.getMinutes()).padStart(2, '0'); // Add leading zero
  const ampm = hours >= 12 ? 'PM' : 'AM';
  hours = hours % 12 || 12; // Convert to 12-hour format
  hours = String(hours).padStart(2, '0'); // Add leading zero

  if (usingFor === 'createdDateInSaveOrder') {
    const seconds = date.getSeconds();
    const hoursIn24Format = date.getHours();
    return `${year}-${month}-${day} ${hoursIn24Format}:${minutes}:${seconds}`;
  } else {
    return `${month}/${day}/${year} ${hours}:${minutes} ${ampm}`;
  }
}

export const formatCurrency = (amount) => {
  const formattedAmount = Math.abs(amount).toLocaleString('en-US', {
    style: 'currency',
    currency: 'USD',
  });
  return amount < 0 ? `-${formattedAmount}` : formattedAmount;
};

export const getRoundValue = (value) =>
  value && !isNaN(value)
    ? parseFloat((Math.round(value * 100) / 100).toFixed(2))
    : 0;

export function prioritizeNumber(numbers) {
  // Helper functions to classify numbers
  const isDecimal = (num) => !Number.isInteger(num);
  const isOdd = (num) => Math.abs(num % 2) === 1;
  const isEven = (num) => num % 2 === 0;

  // Prioritize by categories
  const oddDecimals = numbers.filter(
    (num) => isDecimal(num) && isOdd(Math.floor(num)),
  );
  const oddWholeNumbers = numbers.filter(
    (num) => !isDecimal(num) && isOdd(num),
  );
  const evenDecimals = numbers.filter(
    (num) => isDecimal(num) && isEven(Math.floor(num)),
  );
  const evenWholeNumbers = numbers.filter(
    (num) => !isDecimal(num) && isEven(num),
  );

  // Return the first available number from each category in order of priority
  if (oddDecimals.length > 0) return oddDecimals[0];
  if (oddWholeNumbers.length > 0) return oddWholeNumbers[0];
  if (evenDecimals.length > 0) return evenDecimals[0];
  if (evenWholeNumbers.length > 0) return evenWholeNumbers[0];

  // Return null if no numbers are provided
  return 0;
}

export function adjustPrices(
  products = [],
  diffAmount = 0,
  type,
  discountAppliedMore = false,
  isEligible = () => true,
) {
  // if discountAppliedMore is true then increase the products price and decrease the discount value
  // Sort products by price in descending order
  const normalizedProducts = Array.isArray(products) ? products : [];
  const sortedProducts = [...normalizedProducts].toSorted(
    (a, b) => b.price - a.price,
  );

  // Work with the absolute value for calculation
  let remainingDiff = Math.abs(diffAmount);

  const adjustedProducts = sortedProducts.map((product) => ({
    ...product,
    pennyFixedPrice: product.price,
  }));

  const eligibleIndexes = adjustedProducts.reduce((indexes, product, index) => {
    if (isEligible(product)) indexes.push(index);
    return indexes;
  }, []);

  if (eligibleIndexes.length === 0) return adjustedProducts;

  let i = 0;
  while (remainingDiff > 0) {
    const adjustValue = 0.01;
    const productIndex = eligibleIndexes[i];
    const product = adjustedProducts[productIndex];

    const newPrice = getRoundValue(
      discountAppliedMore
        ? product.pennyFixedPrice + adjustValue
        : product.pennyFixedPrice - adjustValue,
    );

    // Update the remaining difference
    remainingDiff = parseFloat((remainingDiff - adjustValue).toFixed(2));

    // Update the product price and type value
    const typePrice = getRoundValue(
      product.price -
        parseFloat(newPrice.toFixed(2)) +
        parseFloat(product[type]),
    );

    adjustedProducts[productIndex] = {
      ...product,
      price: newPrice,
      pennyFixedPrice: newPrice,
      [type]: typePrice,
    };

    // Move to next product
    i = (i + 1) % eligibleIndexes.length;
  }

  return adjustedProducts;
}

const dealIsStarted = (start) => {
  // Get the current date without time (local time)
  const currDate = new Date().setHours(0, 0, 0, 0);

  // Convert start date to local time without time component
  const startDate = new Date(`${start}T00:00:00`).setHours(0, 0, 0, 0);

  // Return true if the current date is within the range (inclusive)
  return currDate >= startDate;
};

const isDateInRange = (start, end) => {
  // Get the current date without time (local time)
  const currDate = new Date().setHours(0, 0, 0, 0);

  // Convert start and end dates to local time without time component
  const startDate = new Date(`${start}T00:00:00`).setHours(0, 0, 0, 0);
  const endDate = new Date(`${end}T00:00:00`).setHours(0, 0, 0, 0);

  // Return true if the current date is within the range (inclusive)
  return currDate >= startDate && currDate <= endDate;
};

const isTimeInRange = (startTime, endTime) => {
  // if no start time or no end time
  if (!startTime || !endTime) return false;

  // Get the current time in HH:MM:SS format
  const now = new Date();
  const currTime = `${String(now.getHours()).padStart(2, '0')}:${String(
    now.getMinutes(),
  ).padStart(2, '0')}:${String(now.getSeconds()).padStart(2, '0')}`;

  return currTime >= startTime && currTime <= endTime;
};

// new way checking active deals with time scheduling
export const activeDeals = (deals = [], bool = true) => {
  // if no bogo list
  if (deals.length <= 0) return [];

  // returning complete bogo list that we're getting from backend (active_bogo_list -> api)
  if (USING_ACTIVE_BOGO_LIST_API && bool) {
    return deals;
  }

  /**
   * 1 - date range
   * 2 - time range
   * 3 - repeat sequence
   */

  // filtering active bogo list from frontend
  const lists = deals.filter((list) => {
    // Exclude disabled or deleted deals
    if (list.is_disable === '1' || list.is_deleted === '1') return false;

    // if no start date
    if (list.start_date === '0000-00-00') return false;

    // check if start date is later on
    const isDealStarted = dealIsStarted(list.start_date);

    if (!isDealStarted) return false;

    // check date range
    const isInDateRange = isDateInRange(list.start_date, list.end_date);

    if (list.no_end_date === '0' && !isInDateRange) return false;

    // check time range
    const isInTimeRange = isTimeInRange(list.start_time, list.end_time);

    if (list.full_day === '0' && !isInTimeRange) return false;

    // check repeat sequence
    if (list.repeat_type === '0') {
      const allowedDays = list.weekly_days?.split(',');
      // "Sun, Mon, Tue, Wed, Thu, Fri, Sat";
      // "SUNDAY, MONDAY";

      // if no allowed days
      if (allowedDays?.length <= 0) return false;

      const shortForm = allowedDays.map((day) =>
        day.trim().slice(0, 3).toLowerCase(),
      );

      const currentDay = new Intl.DateTimeFormat('en-US', {
        weekday: 'long',
        timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      })
        .format(new Date())
        .slice(0, 3)
        .toLowerCase();

      if (!shortForm.includes(currentDay)) return false;
    }

    return true; // Passes all checks
  });

  return lists && lists.length > 0 ? lists : [];
};

export const handleProductCategoryDiscounts = (
  cart = [],
  discountList = [],
) => {
  return cart;

  /**
   * all cart items normally as it is.
   * first to check all deals which are applicable, product, category, order level deals.
   * apply product, category level deals to all applicable products
   * apply order level deals to all products.
   * return updated cart items ahead.
   */

  // applying Product & Category Level discounts
  const cartAfterProductCategoryLevelDiscounts = cart.map((cartItem) => {
    // mKonnekt owns tobacco items — skip them here (dezi replaces local deals)
    if (cartItem?.isTobaccoDiscountItem) return cartItem;
    const itemId =
      cartItem?.varients && cartItem?.varients?.length > 0
        ? cartItem.variantId
        : cartItem.product.id;
    const categoryIds = cartItem?.product?.cotegory?.split(','); // multiple category ids assigned to product

    let highestDiscount = 0; // storing highest discount value for updating product price
    let dealApplied = null; // storing full deal object for future usage

    const validDeals = activeDeals(discountList, false);

    // find matching deals based on Product ID or Category ID
    const deals = validDeals.filter(
      (d) =>
        d.items.includes(itemId) ||
        categoryIds.some((val) => d.items.includes(val)),
    );

    deals.forEach((deal) => {
      const discount = deal.discount;
      // const discountType = deal.type; // type => product || category || order
      const isPercent = +deal.discount_type === 1; // 1 => percentage || 2 => fixed amount
      const productPrice = cartItem.price;

      // if percent discount then ok pass OR fixed amt discount AND product price is more than discount to be applied
      if (isPercent || (!isPercent && +productPrice > +discount)) {
        const newDiscountValue = isPercent
          ? ((+productPrice * +discount) / 100).toFixed(2)
          : (+discount).toFixed(2);

        if (+newDiscountValue > +highestDiscount) {
          highestDiscount = +newDiscountValue;
          dealApplied = deal;
        }
      }
    });

    return {
      ...cartItem,
      price: cartItem.price - highestDiscount,
      dealApplied,
      dealAppliedDiscount: highestDiscount,
    };
  });

  // filter all order level discounts
  const allOrderLevelDeals = discountList.filter(
    (deal) => deal?.type === 'order',
  );

  const cartAfterOrderLevelDiscounts =
    cartAfterProductCategoryLevelDiscounts.map((cartItem) => {
      // get highest order level discount that can be applied to this product
      // apply only the highest possible order level discount

      const productPrice = cartItem.price;
      let orderLevelDealDiscount = 0; // storing highest discount value for updating product price
      let orderLevelDeal = null; // storing full deal object for future usage

      for (let i = 0; i < allOrderLevelDeals.length; i++) {
        const discount = allOrderLevelDeals[i]?.discount;
        const isPercent = +allOrderLevelDeals[i]?.discount_type === 1; // 1 => percentage || 2 => fixed amount
        // if percent discount then pass OR fixed amt discount AND product price is more than discount to be applied
        if (isPercent || (!isPercent && +productPrice > +discount)) {
          const newDiscountValue = isPercent
            ? ((+productPrice * +discount) / 100).toFixed(2)
            : (+discount).toFixed(2);

          if (+newDiscountValue > +orderLevelDealDiscount) {
            orderLevelDealDiscount = +newDiscountValue;
            orderLevelDeal = allOrderLevelDeals[i];
          }
        }
      }

      return {
        ...cartItem,
        price: cartItem.price - orderLevelDealDiscount,
        orderLevelDeal,
        orderLevelDealDiscount,
      };
    });

  return cartAfterOrderLevelDiscounts;
};

export const cartRowDealDiscount = (item) =>
  getRoundValue(
    (item?.bogoDiscountApplied ? parseFloat(item.bogoDiscount) || 0 : 0) +
      (item?.mixAndMatchDiscountApplied
        ? parseFloat(item.mixAndMatchDiscount) || 0
        : 0),
  );

export const normalizeCartLineItems = (cart = []) => {
  let timeStampCounter = Date.now();
  const singleLineItems = [];

  (Array.isArray(cart) ? cart : []).forEach((item) => {
    const selectedQuantity = Number(item?.selectedQuantity);
    const quantity =
      Number.isFinite(selectedQuantity) && selectedQuantity > 0
        ? selectedQuantity
        : 1;

    if (quantity > 1) {
      for (let i = 0; i < quantity; i++) {
        singleLineItems.push({
          ...item,
          selectedQuantity: 1,
          price: item.price / quantity,
          timeStamp: timeStampCounter++,
          allowCoupon: isCartItemCouponEligible(item),
          inventoryPrice: item.price / quantity,
          ...(item.mkonnektDiscountApplied
            ? {
                mkonnektDiscount:
                  parseFloat(item.mkonnektDiscount || 0) / quantity,
              }
            : {}),
        });
      }
    } else {
      singleLineItems.push({
        ...item,
        inventoryPrice: item.price,
        timeStamp: timeStampCounter++,
        allowCoupon: isCartItemCouponEligible(item),
      });
    }
  });

  return singleLineItems;
};

export const handleBogoDeals = (cart = [], bogoList = []) => {
  const singleLineItems = normalizeCartLineItems(cart);

  // No bogo deals at all (e.g. a newly created store). Return the NORMALIZED
  // line items, not the raw cart — inventoryPrice / timeStamp / allowCoupon are
  // only stamped here, and downstream code depends on them:
  //   - Validation.js sends `inventory_price: element.inventoryPrice ? ... : 0`
  //     so an unstamped cart posts 0 and the order summary renders $0.00
  //   - handleMixAndMatchDeals matches line items by `timeStamp`
  // This mirrors the "deals ran but nothing matched" exit below.
  if (!bogoList?.length) return singleLineItems;

  // filtering only valid bogo lists
  const validBogoList = activeDeals(bogoList);

  let cartAfterBogo = [];

  // looping on valid bogo lists
  validBogoList.forEach((list) => {
    // filtering only this bogo deal products from cart
    const eligibleProducts = singleLineItems?.filter((item) => {
      // mKonnekt owns tobacco items — skip them here (dezi replaces local deals)
      if (
        item?.isTobaccoDiscountItem ||
        item?.isUnavailable ||
        item?.hasHydrationError ||
        item?.isOutOfStock
      ) {
        return false;
      }
      const itemId =
        item?.varients && item?.varients?.length > 0
          ? item.variantId
          : item.product.id;
      return list.items.includes(itemId);
    });

    if (eligibleProducts.length > 0) {
      // adding each deal product in bogo cart with coupon key allowCoupon

      // total qty of this bogo deal products
      const totalQty = eligibleProducts.reduce(
        (acc, curr) => (acc += curr.selectedQuantity),
        0,
      );

      const buyQty = +list.buy_qty; // deal buy qty
      const totalSets = Math.floor(totalQty / buyQty);
      const limit = list.bogo_limit_per_order;
      const cappedSets =
        limit != null && limit !== '' && +limit > 0
          ? Math.min(totalSets, +limit)
          : totalSets;

      // checking if Total qty of this deal products is more than or equal to the deal buy qty
      // if it is more or equal then only apply the deal prices
      if (cappedSets > 0) {
        // sorting based on the addedOn key such that we can form sets line by line
        const sortedByAddedOn = eligibleProducts.toSorted(
          (a, b) => a.addedOn - b.addedOn,
        );

        // forming groups of sets - like 1st group 2nd group in which bogo is applied
        // in cart we will have 1-1 items, so first we set it based on addedOn then we split it in groups
        // based on the buy qty, for eg - 4 items in cart, and 2 buy qty, so 2 sets.
        const groups = [];

        for (let i = 0; i < cappedSets; i++) {
          const startIdx = i * buyQty;
          const endIdx = startIdx + buyQty;
          const singleGroup = sortedByAddedOn.slice(startIdx, endIdx);

          // sorting by desc price
          const sortedGroup = singleGroup.toSorted((a, b) => b.price - a.price);
          groups.push(sortedGroup);
        }

        // const freeQty = +list.free_qty * totalSets; // deal free qty, but this is total free qty
        const freeQty = +list.free_qty; // deal free qty
        const discount = parseFloat(list.discount) || 0; // deal discount amount
        const discountType = parseFloat(list.discount_type) || 2; // deal discount type - 1 = percentage, 2 = flat amount

        // loop on capped sets.. then adjust prices of free qty...
        for (let i = 0; i < cappedSets; i++) {
          const group = groups[i];

          // decrease prices from last products based on free qty from this individual group..
          for (let j = 0; j < freeQty; j++) {
            const lastItem = group.length - 1;
            const itemToUpdate = group[lastItem - j];

            const itemPrice = itemToUpdate.price || 0;

            // on purpose keeping this discountAmount as raw, because in app, bogo discount is not toFixed initially.
            const discountAmount = (itemPrice * discount) / 100; // raw value not rounding up

            const updatedPrice = getRoundValue(itemPrice - discountAmount);

            const newPrice =
              discountType === 2 ? itemPrice - discount : updatedPrice;

            const bogoDiscount =
              itemToUpdate?.bogoDiscount > 0
                ? itemToUpdate.bogoDiscount
                : newPrice <= 0
                  ? itemPrice
                  : parseFloat(getRoundValue(itemPrice - newPrice));

            // this is the product in which we will apply bogo discount
            let newItem = {
              ...itemToUpdate,
              price: newPrice > 0 ? newPrice : 0,
              bogoDiscountApplied: true,
              bogoDiscount,
              bogoId: list?.id,
              bogoDealName: list?.deal_name || list?.title || '',
              altria_funded_discount: list?.altria_funded_discount || 0,
              bogoPromotionCode: list?.promotion_code || '',
            };

            const productExists = cartAfterBogo.find(
              (item) => item.timeStamp === newItem.timeStamp,
            );

            if (productExists) {
              cartAfterBogo = cartAfterBogo.map((item) =>
                item.timeStamp === newItem.timeStamp ? newItem : item,
              );
            } else {
              cartAfterBogo.push(newItem);
            }

            // adding bogo id to other deal products except the bogo discount applied one's
            for (const bogoElement of group) {
              const exists = cartAfterBogo.find(
                (element) => element.timeStamp === bogoElement.timeStamp,
              );
              if (!exists) {
                cartAfterBogo.push({ ...bogoElement, bogoId: list?.id, bogoDealName: list?.deal_name || list?.title || '', altria_funded_discount: list?.altria_funded_discount || 0, bogoPromotionCode: list?.promotion_code || '' });
              }
            }
          }
        }
      }
    }
  });

  if (!(cartAfterBogo.length > 0)) {
    return singleLineItems;
  }

  const finalCart = singleLineItems.map((item) => {
    const takeFromBogoCart = cartAfterBogo.find((bogoItem) => {
      return bogoItem.timeStamp === item.timeStamp;
    });

    return takeFromBogoCart ? takeFromBogoCart : item;
  });

  return finalCart;
};

export const handleMixAndMatchDeals = (cart = [], mixAndMatchList = []) => {
  const activeDeals = mixAndMatchList.filter(
    ({ is_enable }) => is_enable === '1',
  );

  if (activeDeals?.length <= 0) return cart;

  // Deep clone the cart to avoid mutating the original
  const originalCart = cart.map((item) => ({ ...item }));

  let cartAfterMixAndMatch = [];

  activeDeals.forEach((list) => {
    const eligibleProducts = cart?.filter((item) => {
      // mKonnekt owns tobacco items — skip them here (dezi replaces local deals)
      if (
        item?.isTobaccoDiscountItem ||
        item?.isUnavailable ||
        item?.hasHydrationError ||
        item?.isOutOfStock
      ) {
        return false;
      }
      const itemId =
        item?.varients && item?.varients?.length > 0
          ? item.variantId
          : item.productId;
      return list.items_id.includes(itemId);
    });

    const buyQty = +list.min_qty; // deal buy qty

    // if deal products are same or more than deal buy qty then only apply deal discount
    if (eligibleProducts.length > 0 && eligibleProducts.length >= buyQty) {
      const discount = parseFloat(list.discount); // deal discount amount
      const discountType = parseFloat(list.is_percent); // deal discount is in percent true = 1

      const getItemPrice = (element) =>
        (element.pennyFixedPrice ? element.pennyFixedPrice : element.price) ||
        0;

      const limit = list.max_qty;
      const maxDiscountableUnits =
        limit != null && limit !== '' && +limit > 0
          ? Math.min(+limit, eligibleProducts.length)
          : eligibleProducts.length;

      const sortedByPriceAsc = eligibleProducts.toSorted(
        (a, b) => getItemPrice(a) - getItemPrice(b),
      );
      const productsToDiscount = sortedByPriceAsc.slice(0, maxDiscountableUnits);

      // loop over lowest-priced deal products up to max_qty & apply discount to each
      productsToDiscount.forEach((element) => {
        const itemPrice = getItemPrice(element);
        let newPrice =
          discountType === 0
            ? itemPrice - discount
            : // : itemPrice - parseFloat((itemPrice * discount) / 100).toFixed(2);
              (itemPrice - parseFloat((itemPrice * discount) / 100)).toFixed(2);

        // incase of discount is more than item price, then set price to 0.01
        if (+newPrice <= 0) {
          newPrice = 0.01;
        }

        // this is the updated product in which we will apply bogo discount
        const newItem = {
          ...element,
          price: newPrice,
          mixAndMatchDiscountApplied: true,
          mixAndMatchDiscount: parseFloat(
            parseFloat(itemPrice - newPrice).toFixed(2),
          ),
          mixAndMatchId: list?.id,
          mixAndMatchDealName: list?.deal_name || list?.title || '',
          altria_funded_discount: list?.altria_funded_discount || 0,
          mixAndMatchPromotionCode: list?.promotion_code || '',
        };

        cartAfterMixAndMatch.push(newItem);
      });
    }
  });

  // if no matching products with mix and match deal return cart as it is
  if (cartAfterMixAndMatch.length <= 0) {
    return originalCart;
  }

  // if there are some products that has mix & match discount applied, replace them inside cart
  const finalCart = originalCart.map((item) => {
    const takeFromMixAndMatchCart = cartAfterMixAndMatch.find(
      (mixAndMatchItem) => {
        return mixAndMatchItem.timeStamp === item.timeStamp;
      },
    );

    return takeFromMixAndMatchCart ? takeFromMixAndMatchCart : item;
  });

  return finalCart;
};

// mKonnekt (dezi) tobacco discounts, keyed by product UPC.
// CONFIRMED with mKonnekt: `amount` is per-LINE, not per-unit. e.g. 3 units of a
// UPC with a 3.00 allowance => line total 3*10 = 30, minus 3 = 27 (not minus 9).
// Kept as a constant in case the vendor ever changes the convention.
const MKONNEKT_AMOUNT_IS_PER_UNIT = false;

// Applies mKonnekt itemDiscounts onto matching cart line items (by upcCode).
// Runs FIRST in the discount pipeline so mKonnekt-controlled (tobacco) items
// can be excluded from the local BOGO / mix-and-match / category handlers.
export const handleMkonnektDiscounts = (cart = [], mkonnektData = {}) => {
  // mKonnekt takes precedence ONLY when it actually returns money off. Keep
  // just the entries with a positive amount; a UPC that comes back with 0 —
  // like an empty response or an API failure (both yield an empty array) — is
  // treated as "no mKonnekt discount", so the merchant's local BOGO / mix-and-
  // match deals apply to it normally.
  const itemDiscounts = (mkonnektData?.itemDiscounts || []).filter(
    (d) => (parseFloat(d?.amount) || 0) > 0,
  );
  if (!Array.isArray(cart) || cart.length <= 0 || itemDiscounts.length <= 0) {
    return cart;
  }

  // group discount entries by UPC (a UPC may have several discount types)
  const discountsByUpc = itemDiscounts.reduce((acc, d) => {
    const upc = normalizeMkonnektUpc(d?.itemUPC);
    if (!upc) return acc;
    (acc[upc] = acc[upc] || []).push(d);
    return acc;
  }, {});

  // mKonnekt's `amount` is the allowance for the ENTIRE line of that UPC (not
  // per unit). But the same UPC can span MULTIPLE cart lines — e.g. 3 separate
  // qty-1 entries — and applying the full amount to each line would multiply
  // the discount (a $9.75 allowance on 3 lines wrongly becomes $29.25). Sum the
  // quantity per UPC so the per-UPC allowance can be spread proportionally and
  // applied exactly ONCE overall.
  const totalQtyByUpc = {};
  cart.forEach((item) => {
    const upc = normalizeMkonnektUpc(item?.upcCode);
    if (upc && discountsByUpc[upc]) {
      totalQtyByUpc[upc] =
        (totalQtyByUpc[upc] || 0) + (+item.selectedQuantity || 1);
    }
  });

  // Pre-compute each line's proportional share of its UPC allowance. Rounding
  // each share to cents can drift from the true allowance (e.g. 10.00 / 3 =>
  // 3.33 * 3 = 9.99); assign the leftover cent(s) to the FIRST line of each UPC
  // so the per-UPC total is always exact.
  const lineDiscountByIndex = {};
  const upcAccum = {};
  cart.forEach((item, idx) => {
    const upc = normalizeMkonnektUpc(item?.upcCode);
    const discounts = upc ? discountsByUpc[upc] : null;
    if (!discounts || discounts.length <= 0) return;

    const qty = +item.selectedQuantity || 1;
    const totalQty = totalQtyByUpc[upc] || qty;
    const upcAllowance = discounts.reduce((sum, d) => {
      const amt = parseFloat(d?.amount) || 0;
      return sum + (MKONNEKT_AMOUNT_IS_PER_UNIT ? amt * totalQty : amt);
    }, 0);
    const share = parseFloat((upcAllowance * (qty / totalQty)).toFixed(2));
    lineDiscountByIndex[idx] = share;
    if (!upcAccum[upc]) {
      upcAccum[upc] = { allocated: 0, firstIndex: idx, allowance: upcAllowance };
    }
    upcAccum[upc].allocated = parseFloat(
      (upcAccum[upc].allocated + share).toFixed(2),
    );
  });
  // hand the rounding remainder to each UPC's first line
  Object.values(upcAccum).forEach(({ allocated, firstIndex, allowance }) => {
    const remainder = parseFloat((allowance - allocated).toFixed(2));
    if (remainder !== 0) {
      lineDiscountByIndex[firstIndex] = parseFloat(
        ((lineDiscountByIndex[firstIndex] || 0) + remainder).toFixed(2),
      );
    }
  });

  return cart.map((item, idx) => {
    const upc = normalizeMkonnektUpc(item?.upcCode);
    const discounts = upc ? discountsByUpc[upc] : null;
    if (!discounts || discounts.length <= 0) return item;

    const lineDiscount = lineDiscountByIndex[idx] || 0;

    const currentPrice = parseFloat(item.price) || 0;
    let newPrice = parseFloat((currentPrice - lineDiscount).toFixed(2));
    // A manufacturer allowance can cover the full line price. Clamp only
    // negative results so the recorded discount never exceeds the item value.
    if (newPrice < 0) newPrice = 0;
    const appliedDiscount = parseFloat((currentPrice - newPrice).toFixed(2));

    return {
      ...item,
      price: newPrice,
      mkonnektDiscountApplied: true,
      mkonnektDiscount: appliedDiscount,
      mkonnektDiscountBreakdown: discounts.map((d) => ({
        id: d?.id,
        name: d?.name,
        discountType: d?.discountType,
        amount: parseFloat(d?.amount) || 0,
        opCompanyCode: d?.opCompanyCode,
        // Full per-UPC entries (never scaled when rows are split) — the backend
        // de-dupes by itemUPC + id when reconciling the saved order.
        itemUPC: normalizeMkonnektUpc(d?.itemUPC),
        couponCode: d?.couponCode ?? '',
      })),
      // marks this item as owned by mKonnekt so local deals skip it (replace)
      isTobaccoDiscountItem: true,
      allowCoupon: false,
      altria_funded_discount: appliedDiscount,
    };
  });
};

// updating local storage cart
export function updateCart(cartKey, productId, type, cartProductId) {
  // for local storage cart
  let cartData = JSON.parse(localStorage.getItem(cartKey)) || [];

  // const newItem = cartData.find((item) => item.productId === productId);
  const newItem = cartData.find((item) => item.cartProductId === cartProductId);
  if (!newItem) return;

  if (type === 'increment') {
    // for increment simply add a new item in the bogo cart to make it 1 - 1 line items
    const addedOn =
      cartData.length > 0 ? cartData[cartData.length - 1].addedOn + 1 : 1;
    cartData.push({
      ...newItem,
      addedOn,
    });
    localStorage.setItem(cartKey, JSON.stringify(cartData));
  } else {
    // for decrement - delete the last one added from this same Product id
    const matchingProducts = cartData.filter(
      (item) => item.cartProductId === cartProductId,
    );
    const lastProduct = matchingProducts[matchingProducts.length - 1];
    const updatedCartData = cartData.filter(
      (item) => item.addedOn !== lastProduct.addedOn,
    );

    localStorage.setItem(cartKey, JSON.stringify(updatedCartData));
  }
}

// helper function for deducting coupon discount
export const deductCouponDiscount = (price, percentage) => {
  const discount = getRoundValue((price * percentage) / 100);
  const newPrice = getRoundValue(price - discount);

  // when 50% discount, newPrice is less than discount, so taking discount value only...
  // return +percentage === 50 ? discount : newPrice;
  return newPrice;
};

export const defaultTracyLatLng = {
  lat: '30.144028',
  lng: '-96.395654',
  city: 'Tracy',
  zip: '95304',
};

// Age confirmation (storefront) helpers.
// NOTE: This is a frontend-only allowlist to bypass the modal for specific embedded storefront domains.
const DEFAULT_DISABLE_AGE_CONFIRMATION_URL_SUBSTRINGS = ['mocannaofallon.com', 'smokeclubfl.com'];

export function getCurrentStorefrontUrl() {
  try {
    // If embedded in an iframe, use the referrer (parent page URL). Otherwise use current location.
    // eslint-disable-next-line no-restricted-globals
    const isEmbedded = window.location !== window.parent.location;
    return isEmbedded ? document.referrer : document.location.href;
  } catch (e) {
    return '';
  }
}

export function isAgeConfirmationDisabled(
  url = getCurrentStorefrontUrl(),
  disabledUrlSubstrings = DEFAULT_DISABLE_AGE_CONFIRMATION_URL_SUBSTRINGS,
) {
  if (!url) return false;
  if (
    !Array.isArray(disabledUrlSubstrings) ||
    disabledUrlSubstrings.length === 0
  )
    return false;

  const u = String(url).toLowerCase();
  return disabledUrlSubstrings.some((s) => u.includes(String(s).toLowerCase()));
}

export const sendViewLog = async ({
  merchant_id,
  product_id,
  variant_id,
  category_id,
  user_id,
  signal,
}) => {
  try {
    let _uuid = sessionStorage.getItem('uuid');
    if (!_uuid) {
      _uuid = crypto.randomUUID();
      sessionStorage.setItem('uuid', _uuid);
    }

    const data = {
      merchant_id,
      product_id,
      variant_id,
      category_id,
      user_id,
      uuid: _uuid,
    };
    const viewLogResponse = await axios.post(
      BASE_URL_NODE + PRODUCT_COUNT_LOG,
      data,
      {
        headers: { 'Content-Type': 'multipart/form-data' },
        signal,
        validateStatus: () => true,
      },
    );

  } catch (error) {
  }
};

export function getTimeForZone(timeZone) {
  // Fallback chain: provided -> browser's resolved zone -> America/Los_Angeles.
  // Empty string and undefined both throw RangeError in Intl.DateTimeFormat,
  // so resolve to a usable IANA value before constructing the formatters.
  let zone = typeof timeZone === 'string' && timeZone.trim() ? timeZone.trim() : null;
  if (!zone) {
    try {
      zone = Intl.DateTimeFormat().resolvedOptions().timeZone || 'America/Los_Angeles';
    } catch {
      zone = 'America/Los_Angeles';
    }
  }

  const now = new Date();

  let dayName;
  let timeParts;
  try {
    dayName = new Intl.DateTimeFormat('en-US', {
      timeZone: zone,
      weekday: 'long',
    }).format(now);

    timeParts = new Intl.DateTimeFormat('en-US', {
      timeZone: zone,
      hour: '2-digit',
      minute: '2-digit',
      hour12: false,
    }).formatToParts(now);
  } catch {
    // Provided zone wasn't a valid IANA value either - fall back to LA.
    dayName = new Intl.DateTimeFormat('en-US', {
      timeZone: 'America/Los_Angeles',
      weekday: 'long',
    }).format(now);
    timeParts = new Intl.DateTimeFormat('en-US', {
      timeZone: 'America/Los_Angeles',
      hour: '2-digit',
      minute: '2-digit',
      hour12: false,
    }).formatToParts(now);
  }

  const hours = timeParts.find((p) => p.type === 'hour').value;
  const minutes = timeParts.find((p) => p.type === 'minute').value;
  const curr_time = `${hours}:${minutes}`; // "HH:mm"

  return { dayName, curr_time };
}

export const formatPhoneNumber = (value) => {
  if (!value) return value;

  const cleaned = value.replace(/\D/g, '');

  const length = cleaned.length;

  if (length < 4) return cleaned;
  if (length < 7) return `(${cleaned.slice(0, 3)}) ${cleaned.slice(3)}`;
  return `(${cleaned.slice(0, 3)}) ${cleaned.slice(3, 6)}-${cleaned.slice(
    6,
    10,
  )}`;
};
